import { supabase } from "../lib/supabase";

/**
 * Rule text lookup for inline citations.
 *
 * Mirrors `cards.ts`: the answer already carries the cited refs (`meta.sources`),
 * so we fetch just the section text on demand from the public `rule_sections`
 * table and cache it for the session.
 */

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

/** Rule text already fetched this session, or null if it has not been fetched. */
export function getCachedRuleText(ruleRef: string): string | null {
  return cache.get(ruleRef) ?? null;
}

/**
 * Load the text of a single rule section by ref. Cached for the session, and
 * concurrent callers share one request, so a ref is fetched at most once.
 */
export function fetchRuleText(ruleRef: string): Promise<string> {
  const cached = cache.get(ruleRef);
  if (cached !== undefined) return Promise.resolve(cached);

  const pending = inflight.get(ruleRef);
  if (pending) return pending;

  const request = loadRuleText(ruleRef)
    .then((text) => {
      cache.set(ruleRef, text);
      inflight.delete(ruleRef);
      return text;
    })
    .catch((error: unknown) => {
      inflight.delete(ruleRef);
      throw error;
    });

  inflight.set(ruleRef, request);
  return request;
}

async function loadRuleText(ruleRef: string): Promise<string> {
  const { data, error } = await supabase
    .from("rule_sections")
    .select("text")
    .eq("ref", ruleRef)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data?.text as string | undefined) ?? "";
}
